import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { AccessControlCode } from '@shared/constants';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { CheckCircle2, Cpu, Pencil, RefreshCw, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { isElectron } from '@/renderer/util';
import {
  fetchEbmConfig,
  getEbmHealth,
  GetHealthIpcResult,
  hasEbmHostedApiKey,
  initEbmDevice,
  InitDeviceIpcResult,
  OutletEbmConfig,
} from '@/renderer/services/ebmConfigService';
import { apiError } from '@/renderer/util/notify';
import {
  Button,
  EmptyState,
  OutletIndicator,
  ReadOnlyAccessNotice,
} from '@components/common';
import { selectHasAccessControl } from '@/renderer/store/auth/authSelectors';
import { fetchCurrentUser } from '@/renderer/store/auth/authThunks';
import { EbmSetupModal } from './EbmSetupModal';

function formatTimestamp(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

const STATUS_BADGE: Record<
  OutletEbmConfig['activation_status'],
  { label: string; className: string }
> = {
  NOT_CONFIGURED: {
    label: 'Not configured',
    className: 'bg-gray-100 text-gray-600',
  },
  PENDING_INIT: {
    label: 'Pending activation',
    className: 'bg-amber-100 text-amber-700',
  },
  ACTIVE: { label: 'Active', className: 'bg-green-100 text-green-700' },
  FAILED: { label: 'Activation failed', className: 'bg-red-100 text-red-700' },
};

export const EbmConfigurationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const canManage = useAppSelector(
    selectHasAccessControl(AccessControlCode.SETTINGS_EBM_CONFIG_MANAGE),
  );
  const outletId = useAppSelector(selectEffectiveOutletId);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);

  const [config, setConfig] = useState<OutletEbmConfig | null>(null);
  const [hasKey, setHasKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [activationResult, setActivationResult] =
    useState<InitDeviceIpcResult | null>(null);
  const [healthResult, setHealthResult] = useState<GetHealthIpcResult | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!outletId) return;
    setIsLoading(true);
    try {
      const [existing, keyResponse] = await Promise.all([
        fetchEbmConfig(outletId),
        isElectron() ? hasEbmHostedApiKey(outletId) : Promise.resolve(null),
      ]);
      setConfig(existing);
      setHasKey(Boolean(keyResponse?.data?.hasKey));
      setHealthResult(
        existing?.last_health_check_at
          ? {
              healthy: Boolean(existing.last_health_ok),
              checkedAt: existing.last_health_check_at,
            }
          : null,
      );
      setActivationResult(null);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to load EBM configuration',
      );
    } finally {
      setIsLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleActivate = async () => {
    if (!outletId || !config?.dvc_srl_no?.trim()) return;
    setIsActivating(true);
    try {
      // Not trimmed — RRA compares dvc_srl_no byte-exact, and some real
      // RRA-issued device serials include a meaningful leading/trailing
      // space that is part of the actual credential.
      const response = await initEbmDevice(outletId, config.dvc_srl_no);
      if (response.success && response.data) {
        setActivationResult(response.data);
        await load();
        // Refresh the cached profile so outlet.is_ebm_active (which gates
        // settlement-time fiscal enforcement) reflects the new status
        // without requiring a re-login.
        await dispatch(fetchCurrentUser());
      } else {
        apiError(response.error || 'Failed to activate device');
      }
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to activate device',
      );
    } finally {
      setIsActivating(false);
    }
  };

  const handleCheckHealth = async () => {
    if (!outletId) return;
    setIsCheckingHealth(true);
    try {
      const response = await getEbmHealth(outletId);
      if (response.success && response.data) {
        setHealthResult(response.data);
      } else {
        apiError(response.error || 'Failed to check EBM health');
      }
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to check EBM health',
      );
    } finally {
      setIsCheckingHealth(false);
    }
  };

  if (!isElectron()) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center">
          <Cpu size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-600">
            EBM device setup is only available in the desktop app.
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Open smartPOS Cloud on the till/back-office computer to configure
            this outlet&apos;s fiscal device.
          </p>
        </div>
      </div>
    );
  }

  const statusBadge = config ? STATUS_BADGE[config.activation_status] : null;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <OutletIndicator description="Configure and activate this outlet's RRA fiscal (EBM) device." />

      {!canManage && (
        <ReadOnlyAccessNotice message="You can view the EBM configuration here, but only authorised roles can change or activate it." />
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">EBM Configuration</h1>
        <p className="text-gray-500 mt-1 text-sm">
          {effectiveOutlet
            ? `Fiscal device setup for ${effectiveOutlet.name}.`
            : 'Select an outlet above to configure its EBM device.'}
        </p>
      </div>

      {!outletId ? null : isLoading ? (
        <div className="flex h-40 items-center justify-center text-sm text-gray-400">
          Loading…
        </div>
      ) : (
        <>
          {config && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-2">
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadge?.className}`}
                >
                  {statusBadge?.label}
                </span>
                {config.last_health_ok !== null && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    {config.last_health_ok ? (
                      <CheckCircle2 size={13} className="text-green-600" />
                    ) : (
                      <XCircle size={13} className="text-red-500" />
                    )}
                    Health: {config.last_health_ok ? 'OK' : 'Unhealthy'}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400">
                Last init: {formatTimestamp(config.last_init_at)}
                {config.last_init_result_cd
                  ? ` (result ${config.last_init_result_cd})`
                  : ''}
              </p>
              <p className="text-xs text-gray-400">
                Last health check:{' '}
                {formatTimestamp(config.last_health_check_at)}
              </p>
            </div>
          )}

          {!config ? (
            <div className="rounded-2xl border border-gray-200 bg-white">
              <EmptyState
                icon="package"
                title="Set up this outlet's EBM device"
                description={
                  canManage
                    ? "Configure how this outlet talks to RRA's fiscal system — deployment mode, TIN, BHF ID, and device serial number."
                    : "This outlet's EBM device hasn't been set up yet. Only an authorised role can complete setup."
                }
                primaryAction={
                  canManage
                    ? {
                        label: 'Set up EBM',
                        onClick: () => setIsSetupModalOpen(true),
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Device Setup
                </h2>
                {canManage && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsSetupModalOpen(true)}
                  >
                    <Pencil size={14} />
                    Edit
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-400">Deployment Mode</p>
                  <p className="font-medium text-gray-900">
                    {config.deployment_mode === 'LOCAL_VSDC'
                      ? 'Local VSDC (on-site device)'
                      : 'Hosted (remote EBM API)'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">
                    {config.deployment_mode === 'LOCAL_VSDC'
                      ? 'VSDC Base URL'
                      : 'Hosted API Key'}
                  </p>
                  <p className="font-medium text-gray-900">
                    {config.deployment_mode === 'LOCAL_VSDC'
                      ? config.vsdc_base_url || '—'
                      : hasKey
                        ? 'Configured'
                        : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">TIN</p>
                  <p className="font-medium text-gray-900">
                    {config.tin || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">BHF ID</p>
                  <p className="font-medium text-gray-900">
                    {config.bhf_id || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Device Serial Number</p>
                  <p className="whitespace-pre font-mono font-medium text-gray-900">
                    {config.dvc_srl_no || '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                Activate Device
              </h2>
              {canManage && (
                <Button
                  size="sm"
                  onClick={handleActivate}
                  disabled={
                    isActivating || !config || !config.dvc_srl_no?.trim()
                  }
                >
                  {isActivating ? 'Activating…' : 'Activate device'}
                </Button>
              )}
            </div>
            {activationResult && (
              <div className="rounded-xl bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                <p>
                  Status:{' '}
                  <span className="font-medium">
                    {activationResult.activationStatus}
                  </span>{' '}
                  (result {activationResult.resultCd})
                </p>
                <p>{activationResult.resultMsg}</p>
                {activationResult.hint && (
                  <p className="text-amber-700">{activationResult.hint}</p>
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">
                Health Check
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCheckHealth}
                disabled={isCheckingHealth || !config}
              >
                <RefreshCw
                  size={14}
                  className={isCheckingHealth ? 'animate-spin' : ''}
                />
                {isCheckingHealth ? 'Checking…' : 'Check health'}
              </Button>
            </div>
            {healthResult && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                {healthResult.healthy ? (
                  <CheckCircle2 size={14} className="text-green-600" />
                ) : (
                  <XCircle size={14} className="text-red-500" />
                )}
                <span>
                  {healthResult.healthy ? 'Healthy' : 'Unhealthy'} — checked{' '}
                  {formatTimestamp(healthResult.checkedAt)}
                </span>
                {healthResult.detail && (
                  <span className="text-gray-400">({healthResult.detail})</span>
                )}
              </div>
            )}
          </div>

          <EbmSetupModal
            isOpen={isSetupModalOpen}
            onClose={() => setIsSetupModalOpen(false)}
            outletId={outletId}
            existingConfig={config}
            hasKey={hasKey}
            onSaved={(saved, keyResult) => {
              setConfig(saved);
              if (keyResult) setHasKey(keyResult.hasKey);
              setIsSetupModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};

export default EbmConfigurationPage;
